import {
    BenefitCodeEnum,
    BenefitTypeEnum,
    CompanyBenefitDTO,
    CompanyBenefitEnrollment,
    EligibilityRule,
    IchraContributionClassData,
    EmploymentEnum,
    CompensationEnum,
    FamilyStatusEnum
} from "@vitable/asclepius-api-client-ts/api"
import { asDateTime, toCompactDateTimeStr } from "./time-utils"
import { DateTime } from "luxon"
import { Nullable } from "src/types"

export const VPC_STEP = "vpc" as const
export type VpcStep = typeof VPC_STEP

export const ENROLLMENT_BENEFIT_STEP_ORDER: readonly (BenefitTypeEnum | VpcStep)[] = [
    BenefitTypeEnum.Medical,
    VPC_STEP,
    BenefitTypeEnum.Dental,
    BenefitTypeEnum.Vision
] as const

export const VPC_ONLY_BENEFIT_CODES: readonly BenefitCodeEnum[] = [
    BenefitCodeEnum.Vpc,
    BenefitCodeEnum.VpcCore
] as const

export const ICHRA_BENEFIT_CODES: readonly BenefitCodeEnum[] = [
    BenefitCodeEnum.Ichra,
    BenefitCodeEnum.IchraPremiumPlus,
    BenefitCodeEnum.IchraReimbursementOnly
] as const

export const ICHRA_AUTOMATED_BANKING_BENEFIT_CODES: readonly BenefitCodeEnum[] = [
    BenefitCodeEnum.Ichra,
    BenefitCodeEnum.IchraPremiumPlus
] as const

export const MEC_PLUS_BENEFIT_CODES: readonly BenefitCodeEnum[] = [
    BenefitCodeEnum.Mec2,
    BenefitCodeEnum.MecPlus
] as const

export const MVP_BENEFIT_CODES: readonly BenefitCodeEnum[] = [
    BenefitCodeEnum.Mvp,
    BenefitCodeEnum.Mvp2,
    BenefitCodeEnum.Mvpsl,
    BenefitCodeEnum.Mvpsl2
] as const

export const LEVEL_FUNDED_BENEFIT_CODES: readonly BenefitCodeEnum[] = [
    BenefitCodeEnum.Mec,
    BenefitCodeEnum.Mec2,
    BenefitCodeEnum.MecPlus,
    BenefitCodeEnum.Mvp,
    BenefitCodeEnum.Mvp2,
    BenefitCodeEnum.Mvpsl,
    BenefitCodeEnum.Mvpsl2
] as const

/**
 * Determines if given Company Benefit is for an ICHRA Benefit.
 *
 * @param benefit the company benefit to be checked
 *
 * @returns true if ICHRA; otherwise false
 */
export function isIchraBenefit(benefit: CompanyBenefitDTO): boolean {
    return ICHRA_BENEFIT_CODES.includes(benefit.type)
}

/**
 * Member-facing name for a benefit enrollment. ICHRA-family benefits
 * display as "Employer health stipend" because members don't recognize
 * "ICHRA" (the literal value of `benefit_name` on those codes); all
 * other benefits render `benefit_name` as-is.
 *
 * @param benefit a benefit enrollment shape exposing `benefit_code` and `benefit_name`
 */
export function displayBenefitName(benefit: { benefit_code: BenefitCodeEnum; benefit_name: string }): string {
    return ICHRA_BENEFIT_CODES.includes(benefit.benefit_code) ? "Employer health stipend" : benefit.benefit_name
}

/**
 * Determines if given Company Benefit is for an ICHRA Benefit with automated banking enabled.
 *
 * @param benefit the company benefit to be checked
 *
 * @returns true if ICHRA with automated banking; otherwise false
 */
export function isIchraAutomatedBankingBenefit(benefit: CompanyBenefitDTO): boolean {
    return ICHRA_AUTOMATED_BANKING_BENEFIT_CODES.includes(benefit.type)
}

/**
 * Determines if given Company Benefit requires an external plan.
 *
 * @param benefit the benefit to be checked
 *
 * @returns true if the benefit requires an external plan; otherwise false
 */
export function isBenefitRequiringExternalPlan(benefit: CompanyBenefitDTO): boolean {
    return benefit.requires_external_plan_in
}

/**
 * Determines if given Company Benefit is for a VPC-only Benefit.
 *
 * @param benefit the company benefit to be checked
 *
 * @returns true if VPC-only; otherwise false
 */
export function isVpcOnlyBenefit(benefit: CompanyBenefitDTO): boolean {
    return VPC_ONLY_BENEFIT_CODES.includes(benefit.type)
}

/**
 * Determines if given Company Benefit is for a MEC Plus Benefit.
 *
 * @param benefit the company benefit to be checked
 *
 * @returns true if MEC Plus; otherwise false
 */
export function isMecPlusBenefit(benefit: CompanyBenefitDTO): boolean {
    return MEC_PLUS_BENEFIT_CODES.includes(benefit.type)
}

/**
 * Determines if given Company Benefit is for an MVP Benefit.
 *
 * @param benefit the company benefit to be checked
 *
 * @returns true if MVP; otherwise false
 */
export function isMvpBenefit(benefit: CompanyBenefitDTO): boolean {
    return MVP_BENEFIT_CODES.includes(benefit.type)
}

/**
 * Determines if given enrollment is for a VPC-Only Benefit.
 *
 * @param enrollment the enrollment to be checked
 *
 * @returns true if VPC-Only; otherwise false
 */
export function isEnrollmentForVpcOnlyBenefit(enrollment: CompanyBenefitEnrollment): boolean {
    return VPC_ONLY_BENEFIT_CODES.includes(enrollment.benefit_code)
}

/**
 * Determines if given enrollment is for a level-funded Benefit.
 *
 * @param enrollment the enrollment to be checked
 *
 * @returns true if level-funded; otherwise false
 */
export function isEnrollmentForLevelFundedBenefit(enrollment: CompanyBenefitEnrollment): boolean {
    return LEVEL_FUNDED_BENEFIT_CODES.includes(enrollment.benefit_code)
}

/**
 * Determines if given enrollment is for a legacy MEC 1.0 Benefit.
 *
 * @param enrollment the enrollment to be checked
 *
 * @returns true if MEC 1.0; otherwise false
 */
export function isEnrollmentForLegacyMec1Benefit(enrollment: CompanyBenefitEnrollment): boolean {
    return enrollment.benefit_code === BenefitCodeEnum.Mec
}

/**
 * Determines if given enrollment was issued during the Open Enrollment period.
 *
 * @param enrollment the enrollment to be checked
 *
 * @returns true if issued during the OE period; otherwise false
 */
export function wasIssuedDuringOpenEnrollmentPeriod(enrollment: CompanyBenefitEnrollment): boolean {
    const openEnrollmentEndDateStr: string | null = enrollment.benefit_open_enrollment_end

    // Continuous benefits will always be considered in OE
    if (!openEnrollmentEndDateStr) {
        return true
    }

    return asDateTime(enrollment.created) <= asDateTime(openEnrollmentEndDateStr)
}

export function getReadableOpenEnrollmentDateRange(companyBenefit: CompanyBenefitDTO) {
    const start = toCompactDateTimeStr(companyBenefit.open_enrollment_start)
    if (!companyBenefit.open_enrollment_end) return start

    const end = toCompactDateTimeStr(companyBenefit.open_enrollment_end!)
    const timezone = DateTime.fromISO(companyBenefit.open_enrollment_start).toFormat("ZZZZ")
    return `${start} - ${end} ${timezone}`
}

export function getReadableLocationValueFromEligibilityRuleOrContributionClass(
    ruleOrClass: EligibilityRule | IchraContributionClassData
): Nullable<string> {
    if (!ruleOrClass.location_value) return null
    return ruleOrClass.location_value.split(",").join(", ")
}

export function getReadableEmployment(employment?: EmploymentEnum, defaultValue: string = "All"): string {
    if (!employment || employment === EmploymentEnum.Unspecified) return defaultValue
    return employment
}

export function getReadableCompensation(compensation?: CompensationEnum, defaultValue: string = "All"): string {
    if (!compensation || compensation === CompensationEnum.Unspecified) return defaultValue
    if (compensation === CompensationEnum.Salary) return "Salaried"
    return compensation
}

export function getReadableFamilyStatus(familyStatus?: FamilyStatusEnum, defaultValue: string = "All"): string {
    if (!familyStatus || familyStatus === FamilyStatusEnum.Unspecified) return defaultValue
    return familyStatus
}

export function getReadableEligibilityRuleStr(rule: EligibilityRule): string {
    let str = ""
    const isEmploymentSet = !!rule.employment && rule.employment !== EmploymentEnum.Unspecified
    const isCompensationSet = !!rule.compensation && rule.compensation !== CompensationEnum.Unspecified

    if (isEmploymentSet) str += getReadableEmployment(rule.employment)
    if (isEmploymentSet && isCompensationSet) str += " / "
    if (isCompensationSet) str += getReadableCompensation(rule.compensation)

    if (!isEmploymentSet && !isCompensationSet) str += `All Employees`
    else {
        str = `All <b>${str}</b> Employees`
    }

    if (rule.location_value) {
        str += " in "
        str += `<b>${getReadableLocationValueFromEligibilityRuleOrContributionClass(rule)}</b>`
    }

    return str
}
